/**
 * Context Window Indicator Component
 *
 * Displays a visual indicator of how much of the model's context window
 * is currently filled. Helps users understand when they need to summarize
 * or start a new thread.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { formatTokenCount, getUsageColor, getUsageBackgroundColor } from "@/lib/tokenCounter";
type ContextWindowIndicatorProps = {
  totalTokens: number;
  maxTokens: number;
  percentage: number;
  isNearLimit: boolean;
  isAtLimit: boolean;
  shouldSummarize: boolean;
  modelLabel: string;
};
const ContextWindowIndicator: React.FC<ContextWindowIndicatorProps> = t0 => {
  const $ = _c(34);
  const {
    totalTokens,
    maxTokens,
    percentage,
    isNearLimit,
    isAtLimit,
    shouldSummarize,
    modelLabel
  } = t0;
  getUsageColor(percentage);
  getUsageBackgroundColor(percentage);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-2"><svg className="h-4 w-4 text-foreground/70" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg><span className="text-xs font-medium text-foreground/70">Context Window</span></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== percentage) {
    t2 = percentage.toFixed(1);
    $[1] = percentage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="flex items-center justify-between mb-2">{t1}<span className="text-xs font-bold text-foreground/80">{t2}%</span></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = `${Math.min(100, percentage)}%`;
  let t5;
  if ($[5] !== t4) {
    t5 = <div className="mb-2 h-2 w-full rounded-full bg-white/40"><div className="h-full rounded-full transition-all duration-300 rainbow-progress" style={{
        width: t4
      }} /></div>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== totalTokens) {
    t6 = formatTokenCount(totalTokens);
    $[7] = totalTokens;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== maxTokens) {
    t7 = formatTokenCount(maxTokens);
    $[9] = maxTokens;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <span>{t6} / {t7} tokens</span>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== modelLabel) {
    t9 = <span className="text-foreground/40">{modelLabel}</span>;
    $[14] = modelLabel;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <div className="flex items-center justify-between text-[10px] text-foreground/50">{t8}{t9}</div>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== isAtLimit) {
    t11 = isAtLimit && <div className="mt-2 rounded-md border border-red-600 bg-red-600/10 px-2 py-1.5 text-[10px] text-red-400">{"\uD83D\uDED1 CRITICAL: Context window at limit! Use \"Summarize & Continue\" NOW to avoid errors and hallucinations."}</div>;
    $[19] = isAtLimit;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== isAtLimit || $[22] !== shouldSummarize) {
    t12 = shouldSummarize && !isAtLimit && <div className="mt-2 rounded-md border border-orange-600 bg-orange-600/10 px-2 py-1.5 text-[10px] text-orange-400">{"\u26A0\uFE0F WARNING: Context filling up fast. Strongly recommend \"Summarize & Continue\" to start a new thread."}</div>;
    $[21] = isAtLimit;
    $[22] = shouldSummarize;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== isNearLimit || $[25] !== shouldSummarize) {
    t13 = isNearLimit && !shouldSummarize && <div className="mt-2 rounded-md border border-yellow-600 bg-yellow-600/10 px-2 py-1.5 text-[10px] text-yellow-400">📊 Context window filling up. Consider summarizing soon to maintain quality.</div>;
    $[24] = isNearLimit;
    $[25] = shouldSummarize;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t3 || $[32] !== t5) {
    t14 = <div className="rounded-2xl border border-white/40 bg-white/60 backdrop-blur-md p-3">{t3}{t5}{t10}{t11}{t12}{t13}</div>;
    $[27] = t10;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t3;
    $[32] = t5;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
};
export default ContextWindowIndicator;